import React, { useState } from 'react';
import styled from 'styled-components';
import MyPageLayout from '../../components/mypage/MyPageLayout';
import MyPageBreadcrumb from '../../components/mypage/MyPageBreadcrumb';
import CertificateModal from '../../components/modals/CertificateModal';
import { Certificate } from '../../types/certificate';
import { useCertificates } from '../../hooks/useCertificates';

interface MyPageCertificateProps {}

const MyPageCertificate: React.FC<MyPageCertificateProps> = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState<Certificate | null>(null);
  
  const {
    certificates,
    isLoading,
    error,
    currentPage,
    totalPages,
    fetchCertificates
  } = useCertificates({ initialPageSize: 8 });

  // 검색 필터링
  const filteredCertificates = certificates.filter(certificate =>
    certificate.lecture_title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCertificateClick = (certificate: Certificate) => {
    setSelectedCertificate(certificate);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCertificate(null);
  };

  const handlePageChange = (page: number) => {
    fetchCertificates(page);
  };

  return (
    <MyPageLayout hideFooter={false}>
      <Container>
        <MainContainer>
          {/* 페이지 헤더 */}
          <PageHeader>
            <PageTitle>수료증</PageTitle>
          </PageHeader>

          {/* 브레드크럼과 검색바 */}
          <NavigationBar>
            <MyPageBreadcrumb 
              items={[
                { label: '홈' },
                { label: '마이페이지' },
                { label: '수료증' }
              ]}
            />
            
            {/* 검색 */}
            <SearchContainer>
              <SearchInput
                placeholder="검색어를 입력해주세요."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <SearchIcon>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667ZM14 14l-2.9-2.9" stroke="rgba(55, 56, 60, 0.28)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </SearchIcon>
            </SearchContainer>
          </NavigationBar>

          {/* 수료증 콘텐츠 */}
          <Section>

            {/* 수료증 컨텐츠 */}
            <ContentSection>
              {isLoading ? (
                <LoadingState>
                  <LoadingSpinner />
                  <LoadingText>수료증을 불러오는 중...</LoadingText>
                </LoadingState>
              ) : error ? (
                <ErrorState>
                  <EmptyIcon>
                    <svg width="160" height="160" viewBox="0 0 24 24" fill="none">
                      <path d="M12 9V11M12 15H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0377 2.66667 10.2679 4L3.33975 16C2.56995 17.3333 3.53223 19 5.07183 19Z" stroke="#FF6B6B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </EmptyIcon>
                  <EmptyContent>
                    <EmptyTitle>수료증을 불러올 수 없습니다.</EmptyTitle>
                    <EmptySubtitle>{error}</EmptySubtitle>
                    <RetryButton onClick={() => fetchCertificates(0)}>
                      다시 시도
                    </RetryButton>
                  </EmptyContent>
                </ErrorState>
              ) : filteredCertificates.length > 0 ? (
                <>
                  <CertificateGrid>
                    {filteredCertificates.map((certificate) => (
                      <CertificateCard 
                        key={certificate.person_certification_id}
                        onClick={() => handleCertificateClick(certificate)}
                      >
                        <CertificateImage>
                          <img 
                            src={certificate.template_image_url || '/images/no-image-dummy.png'} 
                            alt={certificate.lecture_title}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = '/images/no-image-dummy.png';
                            }}
                          />
                        </CertificateImage>
                        <CertificateInfo>
                          <CertificateTitle>{certificate.lecture_title}</CertificateTitle>
                          {/* <CertificateDate>
                            발급일: {new Date(certificate.issue_at).toLocaleDateString('ko-KR')}
                          </CertificateDate> */}
                        </CertificateInfo>
                      </CertificateCard>
                    ))}
                  </CertificateGrid>
                  
                  {/* 페이지네이션 */}
                  {totalPages > 1 && (
                    <PaginationContainer>
                      <PaginationButton 
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 0}
                      >
                        이전
                      </PaginationButton>
                      
                      <PageNumbers>
                        {Array.from({ length: totalPages }, (_, index) => (
                          <PageNumber
                            key={index}
                            active={index === currentPage}
                            onClick={() => handlePageChange(index)}
                          >
                            {index + 1}
                          </PageNumber>
                        ))}
                      </PageNumbers>
                      
                      <PaginationButton 
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages - 1}
                      >
                        다음
                      </PaginationButton>
                    </PaginationContainer>
                  )}
                </>
              ) : (
                <EmptyState>
                  <EmptyIcon>
                    <svg width="160" height="160" viewBox="0 0 160 160" fill="none">
                      <path fillRule="evenodd" clipRule="evenodd" d="M43.3332 20.6641C40.0195 20.6641 37.3332 23.3503 37.3332 26.664L37.333 43.9974H13.334C10.0203 43.9974 7.33398 46.6837 7.33398 49.9974C7.33398 87.3958 36.944 117.878 73.9993 119.28V133.997H43.3328C40.0191 133.997 37.3328 136.684 37.3328 139.997C37.3328 143.311 40.0191 145.997 43.3328 145.997H116.666C119.98 145.997 122.666 143.311 122.666 139.997C122.666 136.684 119.98 133.997 116.666 133.997H85.9993V119.28C123.055 117.879 152.665 87.396 152.665 49.9974C152.665 46.6837 149.979 43.9974 146.665 43.9974H122.666L122.666 26.6641C122.666 25.0728 122.034 23.5467 120.909 22.4214C119.784 21.2962 118.258 20.6641 116.666 20.6641H43.3332ZM122.666 55.9974L122.666 61.9962C122.666 71.3466 122.448 77.2226 121.889 81.8549L121.878 81.9446C121.197 86.953 119.996 91.4614 118.159 95.5444C130.348 86.21 138.681 72.0933 140.355 55.9974H122.666ZM41.8399 95.5441C40.0394 91.5039 38.8675 87.0145 38.1917 81.9259L38.1871 81.8918C37.5505 76.8779 37.3328 70.9177 37.3328 61.9961L37.3329 55.9974H19.6441C21.318 72.0931 29.6509 86.2097 41.8399 95.5441ZM49.3328 61.9961L49.3331 32.6641H110.666L110.666 61.996C110.666 71.2363 110.445 76.502 109.981 80.372C108.871 88.4812 106.254 93.9844 101.802 98.4415C96.1203 104.056 88.1147 107.329 79.9993 107.329C71.9602 107.329 63.8073 103.984 58.1968 98.4414C53.7434 93.9831 51.1989 88.6978 50.0894 80.3629C49.5544 76.1396 49.3328 70.8091 49.3328 61.9961Z" fill="#171719"/>
                    </svg>
                  </EmptyIcon>
                  <EmptyContent>
                    <EmptyTitle>
                      {searchQuery ? '검색 결과가 없습니다.' : '수료증 발급을 준비 중입니다.'}
                    </EmptyTitle>
                    {searchQuery && (
                      <EmptySubtitle>다른 검색어로 시도해보세요.</EmptySubtitle>
                    )}
                  </EmptyContent>
                </EmptyState>
              )}
            </ContentSection>
          </Section>
        </MainContainer>
      </Container>
      
      {/* 모달 */}
      <CertificateModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        type="certificate"
        title={selectedCertificate?.lecture_title}
        personCertificationId={selectedCertificate?.person_certification_id}
      />
    </MyPageLayout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
`;

const MainContainer = styled.div`
  width: 100%;
  max-width: 894px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  margin-top: 60px;
`;

const PageTitle = styled.h1`
  color: #171719;
  font-size: 40px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 52px;
  margin: 0;
`;

const NavigationBar = styled.div`
  width: 100%;
  height: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Section = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 24px;
`;

const SearchContainer = styled.div`
  position: relative;
  width: 282px;
  height: 32px; /* 브레드크럼과 같은 높이 설정 */
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 100%;
  padding: 8px 32px 8px 32px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.22);
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.3px;
  color: #171719;
  outline: none;
  box-sizing: border-box;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 8px;
  top: 8px;
  height: 16px;
  padding-left: 2px;
  padding-right: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
`;

const ContentSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 36px;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 160px 0;
  gap: 24px;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #448181;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.span`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
`;

const CertificateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const CertificateCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  cursor: pointer;
`;

const CertificateImage = styled.div`
  width: 100%;
  aspect-ratio: 1;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.08);
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CertificateInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CertificateTitle = styled.h3`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  margin: 0;
`;

const CertificateDate = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 32px;
`;

const PaginationButton = styled.button`
  padding: 8px 16px;
  border: 1px solid rgba(112, 115, 124, 0.22);
  background: white;
  color: #171719;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 8px;
`;

const PageNumber = styled.button<{ active: boolean }>`
  width: 32px;
  height: 32px;
  border: none;
  background: ${props => props.active ? '#448181' : 'transparent'};
  color: ${props => props.active ? 'white' : '#171719'};
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? '#448181' : '#f5f5f5'};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 160px 0;
  gap: 24px;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 160px 0;
  gap: 24px;
`;

const RetryButton = styled.button`
  padding: 12px 24px;
  background: #448181;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 12px;

  &:hover {
    opacity: 0.9;
  }
`;

const EmptyIcon = styled.div`
  width: 160px;
  height: 160px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const EmptyContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const EmptyTitle = styled.h3`
  color: #171719;
  font-size: 20px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 28px;
  text-align: center;
  margin: 0;
`;

const EmptySubtitle = styled.p`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  text-align: center;
  margin: 0;
`;

export default MyPageCertificate;